import {
    api,
    CARD_LABELS,
    type CardLabel,
    ListChoiceFilter,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Button,
    DateTime,
    Field,
    FormSheet,
    Input,
    type ListColumnDef,
    ObjectRef,
    PageHeader,
    SearchableSelect,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { PricingCard, PricingCardSummary } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/cards';
const PAGE_KEY = 'pricing-cards';

export const cardsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const cardFilters = filterDefs(PATH, {
    q: 'Search',
    side: 'Side',
    include_closed: 'Versions',
    range: 'Active from',
});

/** The card's derived label (flat rate, cost plus, mixed); a cost card has none. */
export function CardLabelBadge({ label }: { label: string | null }) {
    return label ? (
        <Badge variant="info">{CARD_LABELS[label as CardLabel] ?? label}</Badge>
    ) : null;
}

/** Who a card prices: the tenant default, or one merchant's negotiated terms. */
export function CardScope({ merchantId }: { merchantId: string | null }) {
    return merchantId ? (
        <ObjectRef id={merchantId} />
    ) : (
        <span className="text-fg-2">Tenant default</span>
    );
}

const PRESETS = {
    fee: [
        { value: 'flat_rate', label: 'Flat rate (2.90% + $0.30)' },
        { value: 'cost_plus', label: 'Cost plus (cost + 0.10% + $0.30)' },
    ],
    cost: [{ value: 'blended_cost', label: 'Blended cost line' }],
} as const;

/**
 * BM-62: a new cost or fee card, from a preset; the rules are edited on the card's page. With a merchant it is that
 * merchant's negotiated card (the tenant default otherwise).
 */
export function NewCardDialog({
    open,
    onOpenChange,
    merchantId = null,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    merchantId?: string | null;
}) {
    const navigate = useNavigate();
    const [side, setSide] = useState<'fee' | 'cost'>('fee');
    const [name, setName] = useState('');
    const [preset, setPreset] = useState<string | null>('flat_rate');
    const [error, setError] = useState<unknown>(null);
    const create = useWrite(
        async () =>
            (
                await api().post<PricingCard>(PATH, {
                    side,
                    name,
                    merchant_id: merchantId,
                    preset,
                })
            ).data,
        [PATH],
    );
    return (
        // BM-84 (rule E.9, design 0.7.0 pricing-cards): the new card in a sheet
        <FormSheet
            open={open}
            onOpenChange={(o) => {
                onOpenChange(o);
                if (!o) setError(null);
            }}
            size="sm"
            title={
                merchantId ? 'New card for this merchant' : 'New pricing card'
            }
            submitLabel="Create card"
            busy={create.isPending}
            submitDisabled={name.trim() === ''}
            dirty={name !== ''}
            error={error}
            data-testid="card-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                create.mutate(undefined, {
                    onSuccess: (card) => {
                        onOpenChange(false);
                        navigate(`/pricing/cards/${card.id}`);
                    },
                    onError: setError,
                });
            }}
        >
            <Field id="card-side" label="Side">
                <SearchableSelect
                    id="card-side"
                    label="Side"
                    value={side}
                    options={[
                        {
                            value: 'fee',
                            label: 'Fee card (what the merchant pays)',
                        },
                        {
                            value: 'cost',
                            label: 'Cost card (what Bemor pays)',
                        },
                    ]}
                    onChange={(v) => {
                        const next = v === 'cost' ? 'cost' : 'fee';
                        setSide(next);
                        setPreset(PRESETS[next][0].value);
                    }}
                />
            </Field>
            <Field id="card-name" label="Name">
                <Input
                    id="card-name"
                    maxLength={80}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Field>
            <Field id="card-preset" label="Start from">
                <SearchableSelect
                    id="card-preset"
                    label="Start from"
                    value={preset}
                    options={PRESETS[side]}
                    onChange={setPreset}
                />
            </Field>
        </FormSheet>
    );
}

/**
 * BM-62 2.6: the payments host's cost cards and fee cards. A card is a list of rules; an edit is a new version, and
 * the merchants on the old version move to it.
 */
export function CardsPage() {
    const can = useAuthStore((s) => s.can);
    const [open, setOpen] = useState(false);
    const columns: ListColumnDef<PricingCardSummary>[] = [
        {
            key: 'name',
            label: 'Card',
            sortKey: 'name',
            boardCard: true,
            locked: 'start',
            csv: (c) => c.name,
            cell: (c) => (
                <Link
                    to={`/pricing/cards/${c.id}`}
                    className="font-medium hover:underline"
                >
                    {c.name}
                </Link>
            ),
        },
        {
            key: 'side',
            label: 'Side',
            sortKey: 'side',
            csv: (c) => c.side,
            cell: (c) => (
                <StatusBadge status={c.side} domain={ADMIN_STATUSES.cardSide} />
            ),
        },
        {
            key: 'label',
            label: 'Shape',
            csv: (c) => c.label ?? '',
            cell: (c) => <CardLabelBadge label={c.label} />,
        },
        {
            key: 'merchant_id',
            aliases: ['scope'],
            label: 'Prices',
            csv: (c) => c.merchant_id ?? 'tenant default',
            cell: (c) => <CardScope merchantId={c.merchant_id} />,
        },
        {
            key: 'version',
            label: 'Version',
            sortKey: 'version',
            csv: (c) => c.version,
            cell: (c) => `v${c.version}${c.active_to ? ' (closed)' : ''}`,
            noTruncate: true,
        },
        {
            key: 'rule_count',
            aliases: ['rules'],
            label: 'Rules',
            csv: (c) => c.rule_count,
            cell: (c) => c.rule_count,
            noTruncate: true,
        },
        {
            key: 'merchants',
            label: 'Merchants',
            csv: (c) => c.merchants,
            cell: (c) => c.merchants,
            noTruncate: true,
        },
        {
            key: 'active_from',
            label: 'Active from',
            kind: 'datetime',
            sortKey: 'active_from',
            csv: (c) => c.active_from,
            cell: (c) => <DateTime value={c.active_from} empty="—" />,
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<PricingCardSummary>(PAGE_KEY, PATH, {
        columns,
        filters: cardFilters,
    });
    return (
        <div>
            <PageHeader
                title="Pricing cards"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Fee cards price what the merchant pays, cost cards estimate what Bemor pays. Each is priced once, at capture, from the version in force.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    can(P.pricingManage) ? (
                        <Button onClick={() => setOpen(true)}>New card</Button>
                    ) : undefined
                }
            />
            <AdminListPage
                noun="cards"
                state={state}
                query={query}
                rowKey={(c) => `${c.id}:${c.version}`}
                rowHref={(c) => `/pricing/cards/${c.id}`}
                controls={{
                    q: { kind: 'search' },
                    side: { domain: ADMIN_STATUSES.cardSide },
                    include_closed: (
                        <ListChoiceFilter
                            state={state}
                            name="include_closed"
                            label="Versions"
                            options={[
                                { value: 'true', label: 'Closed versions too' },
                            ]}
                        />
                    ),
                }}
            />
            <NewCardDialog open={open} onOpenChange={setOpen} />
        </div>
    );
}
